import {
    minimalToast,
    NotificationCategory,
    NotificationPriority,
    NotificationSeverity,
} from "../../utils/toast";
import { AxiosRequest } from "../ApiCall";
import { configEnv } from "../constant";
import type { StudentTaskBoardConfig } from "../../constants/studentTaskBoard";

type OrgListQueryOptions = { mastersOnly?: boolean; minimal?: boolean };

function buildOrgListQuery(options?: OrgListQueryOptions): string {
    const params = new URLSearchParams();
    if (options?.mastersOnly !== false) params.set("mastersOnly", "true");
    if (options?.minimal !== false) params.set("minimal", "true");
    const query = params.toString();
    return query ? `?${query}` : "";
}

export const organizationApi = {
    create: async (
        body: {
            name: string;
            location: string;
            adminIds: string[];
            type: string;
            logo?: string;
        },
        header: any
    ) => {
        try {
            const data = await AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/organization/create-organization`,
                body,
                header
            );
            minimalToast({
                title: "Create Organization",
                message: data.data.message,
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.ALERT,
                priority: NotificationPriority.HIGH,
            });
            return data.data;
        } catch (error: any) {
        }
    },
    getAll: async (header: any, options?: OrgListQueryOptions) => {
        try {
            const data = await AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/organization/get-all-organizations${buildOrgListQuery(options)}`,
                null,
                header
            );
            return data.data;
        } catch (error: any) {
        }
    },
    getById: async (id: string, header: any, options: { silentError?: boolean } = {}) => {
        try {
            const data = await AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/organization/get-organization/${id}`,
                null,
                header,
                options
            );
            return data.data;
        } catch (error: any) {
        }
    },
    getAllOrganizationAdmin: async (
        header: any,
        options?: OrgListQueryOptions
    ) => {
        try {
            const data = await AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/organization/get-all-organizations-of-admin${buildOrgListQuery(options)}`,
                null,
                header
            );
            return data.data;
        } catch (error: any) {
        }
    },
    update: async (
        id: string,
        body: { userId?: string, internalWeight?: number, externalWeight?: number, peerWeight?: number, selfWeight?: number },
        header: any
    ) => {
        try {
            const data = await AxiosRequest(
                "PUT",
                `${configEnv.baseUrl}/organization/${id}`,
                body,
                header
            );
            minimalToast({
                title: "Update Organization",
                message: data.data.message,
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.UPDATE,
                priority: NotificationPriority.MEDIUM,
            });
            return data.data;
        } catch (error: any) {
        }
    },
    delete: async (id: string, header: any) => {
        try {
            const data = await AxiosRequest(
                "DELETE",
                `${configEnv.baseUrl}/organization/${id}`,
                null,
                header
            );
            minimalToast({
                title: "Delete Organization",
                message: data.data.message,
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.ALERT,
                priority: NotificationPriority.HIGH,
            });
            return data.data;
        } catch (error: any) {
        }
    },
    renewPlan: async (
        orgId: string,
        body: { billingCycle: string; seats: any },
        header: any
    ) => {
        try {
            const data = await AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/organization/${orgId}/renew-plan`,
                body,
                header
            );
            minimalToast({
                title: "Renew Organization Plan",
                message: data.data.message,
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.UPDATE,
                priority: NotificationPriority.MEDIUM,
            });
            return data.data;
        } catch (error: any) {
            minimalToast({
                title: "Renew Organization Plan",
                message: error.response.data.message,
                type: NotificationSeverity.ERROR,
                category: NotificationCategory.ALERT,
                priority: NotificationPriority.HIGH,
            });
        }
    },
    updatePACTConfig: async (
        id: string,
        body: {
            internalWeight: number;
            externalWeight: number;
            peerWeight: number;
        },
        header: any
    ) => {
        try {
            const data = await AxiosRequest(
                "PUT",
                `${configEnv.baseUrl}/organization/${id}/pact-config`,
                body,
                header
            );
            minimalToast({
                title: "Update PACT Configuration",
                message: data.data.message,
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.UPDATE,
                priority: NotificationPriority.MEDIUM,
            });
            return data.data;
        } catch (error: any) {
            throw error;
        }
    },
    getAIPromptSettings: async (id: string, header: any) => {
        try {
            const data = await AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/organization/${id}/ai-prompt-settings`,
                null,
                header
            );
            return data.data;
        } catch (error: any) {
            throw error;
        }
    },
    updateAIPromptSettings: async (
        id: string,
        body: {
            customInstructions?: string;
            anonymizeNames?: boolean;
            terminology?: {
                auditee?: string;
                teacher?: string;
                employee?: string;
                peer?: string;
            };
            enabled?: boolean;
        },
        header: any
    ) => {
        try {
            const data = await AxiosRequest(
                "PATCH",
                `${configEnv.baseUrl}/organization/${id}/ai-prompt-settings`,
                body,
                header
            );
            minimalToast({
                title: "Update AI Prompt Settings",
                message: data.data.message,
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.UPDATE,
                priority: NotificationPriority.MEDIUM,
            });
            return data.data;
        } catch (error: any) {
            minimalToast({
                title: "Update AI Prompt Settings",
                message: error.response?.data?.message || "Failed to update settings",
                type: NotificationSeverity.ERROR,
                category: NotificationCategory.ALERT,
                priority: NotificationPriority.HIGH,
            });
            throw error;
        }
    },
    updateFeatures: async (
        id: string,
        body: {
            features: {
                imageVerification?: boolean;
                aiAudits?: boolean;
                assessments?: boolean;
                oneTimeAudits?: boolean;
                anecdotalRecords?: boolean;
            };
        },
        header: any
    ) => {
        try {
            const data = await AxiosRequest(
                "PATCH",
                `${configEnv.baseUrl}/organization/${id}/features`,
                body,
                header
            );
            minimalToast({
                title: "Update Organization Features",
                message: data.data.message,
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.UPDATE,
                priority: NotificationPriority.MEDIUM,
            });
            return data.data;
        } catch (error: any) {
            minimalToast({
                title: "Update Organization Features",
                message: error.response?.data?.message || "Failed to update organization features",
                type: NotificationSeverity.ERROR,
                category: NotificationCategory.ALERT,
                priority: NotificationPriority.HIGH,
            });
            throw error;
        }
    },
    /**
     * Every feature setting the caller may manage for one organization, with the
     * value in force after inheritance.
     */
    getFeatureSettings: async (id: string, header: any) => {
        const res = await AxiosRequest(
            "GET",
            `${configEnv.baseUrl}/organization/${id}/feature-settings`,
            null,
            header
        );
        return res.data;
    },

    /**
     * Turn one registered feature on or off.
     *
     * Separate from updateCustomization, which is P3M Super Admin only because
     * it writes any key of the customization map. This writes one registered
     * feature in the shape that feature declares, which is what lets an
     * organization's own Super Admin or Owner manage it.
     */
    updateFeatureSetting: async (
        id: string,
        input: { key: string; label?: string; enabled: boolean; mode?: string },
        header: any
    ) => {
        try {
            const data = await AxiosRequest(
                "PATCH",
                `${configEnv.baseUrl}/organization/${id}/feature-settings`,
                { key: input.key, enabled: input.enabled, mode: input.mode },
                header
            );
            minimalToast({
                title: input.label || "Organization settings",
                message: input.enabled ? "Turned on." : "Turned off.",
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.UPDATE,
                priority: NotificationPriority.MEDIUM,
            });
            return data.data;
        } catch (error: any) {
            minimalToast({
                title: input.label || "Organization settings",
                message:
                    error.response?.data?.error?.message ||
                    "Failed to update this setting",
                type: NotificationSeverity.ERROR,
                category: NotificationCategory.ALERT,
                priority: NotificationPriority.HIGH,
            });
            throw error;
        }
    },

    updateCustomization: async (
        id: string,
        body: {
            customization: {
                customAssessmentInstruction?: boolean;
                studentTaskBoard?: StudentTaskBoardConfig;
                // Not every customization is a flag: some, like the student task
                // board, are structured objects.
                [key: string]: boolean | StudentTaskBoardConfig | undefined;
            };
        },
        header: any
    ) => {
        try {
            const data = await AxiosRequest(
                "PATCH",
                `${configEnv.baseUrl}/organization/${id}/customization`,
                body,
                header
            );
            minimalToast({
                title: "Update Organization Customization",
                message: data.data.message,
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.UPDATE,
                priority: NotificationPriority.MEDIUM,
            });
            return data.data;
        } catch (error: any) {
            minimalToast({
                title: "Update Organization Customization",
                message: error.response?.data?.message || "Failed to update organization customization",
                type: NotificationSeverity.ERROR,
                category: NotificationCategory.ALERT,
                priority: NotificationPriority.HIGH,
            });
            throw error;
        }
    },
    updateDetails: async (
        id: string,
        body: {
            logo?: string;
            description?: string;
            name?: string;
            location?: string;
        },
        header: any
    ) => {
        try {
            const data = await AxiosRequest(
                "PATCH",
                `${configEnv.baseUrl}/organization/${id}/details`,
                body,
                header
            );
            minimalToast({
                title: "Update Organization",
                message: data.data.message,
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.UPDATE,
                priority: NotificationPriority.MEDIUM,
            });
            return data.data;
        } catch (error: any) {
            minimalToast({
                title: "Update Organization",
                message: error.response?.data?.message || "Failed to update organization",
                type: NotificationSeverity.ERROR,
                category: NotificationCategory.ALERT,
                priority: NotificationPriority.HIGH,
            });
            throw error;
        }
    },
};

export interface AIPromptSettings {
    customInstructions?: string;
    anonymizeNames?: boolean;
    terminology?: {
        auditee?: string;
        teacher?: string;
        employee?: string;
        peer?: string;
    };
    enabled?: boolean;
}
